import { DEFAULT_LANDING_PAGE_ACCENT_COLOR } from '@consts';
import { GoalLandingPagePreviewVideoModel } from '@models/goals';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { useState } from 'react';
import { parseHighlightedText } from './highlighters';
import { useLandingPageTranslation } from './useLandingPageTranslation';
import {
  getVideoEmbedUrl,
  getYouTubeThumbnailUrl,
  getYouTubeVideoId,
  YouTubeThumbnailQuality
} from './videoEmbed';

export default function LandingPagePreviewVideo({
  previewVideo,
  accentColor = DEFAULT_LANDING_PAGE_ACCENT_COLOR,
  showLabels = true
}: {
  previewVideo: GoalLandingPagePreviewVideoModel | null | undefined;
  accentColor?: string;
  /** When false, renders only the embed (used by the editor, which edits the labels inline). */
  showLabels?: boolean;
}) {
  const { t } = useLandingPageTranslation();
  const [playing, setPlaying] = useState(false);
  const [thumbnailQuality, setThumbnailQuality] =
    useState<YouTubeThumbnailQuality>('maxresdefault');

  const embedUrl = getVideoEmbedUrl(previewVideo, playing);
  const youtubeVideoId = getYouTubeVideoId(previewVideo);
  if (!embedUrl) return <></>;

  // YouTube can't hide its title / "Watch on YouTube" chrome via params, so show a
  // click-to-play facade (thumbnail + our play button) and only mount the iframe on play.
  const showFacade = !!youtubeVideoId && !playing;

  return (
    <div
      className={classNames(
        'w-full max-w-[1200px] mx-auto flex flex-col items-center gap-md @[640px]:gap-lg',
        // RESPONSIVE STYLES
        'p-sm @[768px]:p-md @[1280px]:py-xxxl @[1280px]:px-none'
      )}
    >
      {showLabels && previewVideo?.title && (
        <p
          className={classNames(
            'font-bold leading-[1.1] text-center',
            setupForDark('text-white'),
            // RESPONSIVE STYLES
            'text-[28px] @[480px]:text-[36px] @[640px]:text-[48px] break-words'
          )}
        >
          {parseHighlightedText(previewVideo.title, 'text-white', undefined, accentColor)}
        </p>
      )}
      {showLabels && previewVideo?.subtitle && (
        <p
          className={classNames(
            'font-normal leading-[1.4] text-center text-white/70',
            // RESPONSIVE STYLES
            'text-[16px] @[640px]:text-[20px]'
          )}
        >
          {previewVideo.subtitle}
        </p>
      )}
      <div className='relative w-full max-w-[900px] aspect-video overflow-hidden'>
        {showFacade ? (
          <button
            type='button'
            onClick={() => setPlaying(true)}
            aria-label={
              previewVideo?.title || t('domains.goals.landingPage.previewVideo.playButtonLabel')
            }
            className='group absolute inset-0 h-full w-full cursor-pointer'
          >
            <img
              src={getYouTubeThumbnailUrl(youtubeVideoId, thumbnailQuality)}
              alt={previewVideo?.title || ''}
              onError={() => setThumbnailQuality('hqdefault')}
              className='h-full w-full object-cover'
            />
            <span className='absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/30'>
              <span
                className='flex items-center justify-center rounded-full w-[56px] h-[56px] @[640px]:w-[76px] @[640px]:h-[76px] transition-transform group-hover:scale-105'
                style={{ backgroundColor: accentColor }}
              >
                <svg
                  viewBox='0 0 24 24'
                  fill='white'
                  className='w-[24px] h-[24px] @[640px]:w-[32px] @[640px]:h-[32px] translate-x-[2px]'
                >
                  <path d='M8 5v14l11-7z' />
                </svg>
              </span>
            </span>
          </button>
        ) : (
          <iframe
            src={embedUrl}
            title={previewVideo?.title || t('domains.goals.landingPage.previewVideo.iframeTitle')}
            className='w-full h-full'
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
            allowFullScreen
          />
        )}
      </div>
    </div>
  );
}
